import * as React from "react";
import styled from "styled-components";
import { AccessRightCodes } from "Components/Models/AccessRights";
import { Authorized } from "Routes/Authorized";
import { useLocation } from "react-router-dom";
import { UnstyledLink } from "Components/Shared/Routes/UnstyledLink";

type Props<T extends boolean> = {
  menuItems: SubMenuItem<T>[];
  accessRight?: AccessRightCodes[];
  isHorizontal?: boolean;
};

export type SubMenuItem<T extends boolean> = T extends true
  ? {
      name: string;
      url: string;
    }
  : {
      name: string;
      isActive: boolean;
      onClick: () => void;
    };

const StyledSubMenuItemsWrapper = styled.div<{ isHorizontal?: boolean }>`
  margin-right: ${props => (props.isHorizontal ? 0 : 30)}px;

  ${props =>
    props.isHorizontal &&
    `margin-top: -10px;
  margin-bottom: 12px;`}
`;

const StyledSubMenuItems = styled.ul<{ isHorizontal?: boolean }>`
  padding-left: 0;
  list-style: none;
  margin: 0;
  margin-bottom: ${props => (props.isHorizontal ? 0 : 20)}px;
  ${props =>
    props.isHorizontal &&
    `display: flex;
    border-bottom: 1px solid ${props.theme.colors.borderDark};`}
`;

const StyledSubMenuItem = styled.li<{
  isActive?: boolean;
  isHorizontal?: boolean;
}>`
  color: ${props =>
    props.isActive
      ? props.theme.palette.primary.main
      : props.theme.palette.secondary.main};
  font-weight: ${props => (props.isActive ? 500 : 400)};
  margin: ${props => (props.isHorizontal ? -1 : 8)}px 0;

  ${props => props.isHorizontal && `border-bottom: 2px solid transparent;`}

  ${props =>
    props.isActive &&
    props.isHorizontal &&
    `border-bottom-color: ${props.theme.palette.primary.main};`}

  ${props =>
    props.isHorizontal &&
    `padding: 3px 13px;

      &:first-child {
        padding-left: 3px;
      }

      &:last-child {
        padding-right: 3px;
      }`}

    a {
    &:hover {
      color: ${props => props.theme.palette.text.primary};
    }
  }
`;

const StyledSubMenuClickItem = styled.div`
  cursor: pointer;
`;

const SubMenuItems = <T extends boolean>(props: Props<T>) => {
  const { menuItems, isHorizontal } = props;
  const location = useLocation();

  const isActive = (url: string) => location.pathname.includes(url);

  return (
    <StyledSubMenuItemsWrapper isHorizontal={isHorizontal}>
      <StyledSubMenuItems isHorizontal={isHorizontal}>
        {menuItems.map((menuItem, index) => {
          const linkMenuItem = menuItem as SubMenuItem<true>;
          const clickMenuItem = menuItem as SubMenuItem<false>;
          return (
            <StyledSubMenuItem
              key={index}
              isActive={
                linkMenuItem.url
                  ? isActive(linkMenuItem.url)
                  : clickMenuItem.isActive
              }
              isHorizontal={isHorizontal}
            >
              <div>
                {linkMenuItem.url ? (
                  <UnstyledLink to={linkMenuItem.url}>
                    {menuItem.name}
                  </UnstyledLink>
                ) : (
                  <StyledSubMenuClickItem onClick={clickMenuItem.onClick}>
                    {menuItem.name}
                  </StyledSubMenuClickItem>
                )}
              </div>
            </StyledSubMenuItem>
          );
        })}
        {isHorizontal && <StyledSubMenuItem />}
      </StyledSubMenuItems>
    </StyledSubMenuItemsWrapper>
  );
};

export const SubMenu = <T extends boolean>(props: Props<T>) => {
  const { menuItems, accessRight, isHorizontal } = props;

  return (
    <>
      {accessRight ? (
        <Authorized accessRights={accessRight}>
          <SubMenuItems menuItems={menuItems} isHorizontal={isHorizontal} />
        </Authorized>
      ) : (
        <SubMenuItems menuItems={menuItems} isHorizontal={isHorizontal} />
      )}
    </>
  );
};
